import React, { useState, useEffect } from 'react';
import { localStorageService } from '../utils/localStorage';

interface GitHubTokenInputProps {
  onTokenChange: (token: string) => void;
  onOwnerChange: (owner: string) => void;
  onRepoChange: (repo: string) => void;
  onTicketSourceChange: (source: string) => void;
  onTicketWorkspaceChange: (workspace: string) => void;
}

export const GitHubTokenInput: React.FC<GitHubTokenInputProps> = ({ onTokenChange, onOwnerChange, onRepoChange, onTicketSourceChange, onTicketWorkspaceChange }) => {
  const [token, setToken] = useState('');
  const [owner, setOwner] = useState('');
  const [repo, setRepo] = useState('');
  const [ticketSource, setTicketSource] = useState('linear');
  const [ticketWorkspace, setTicketWorkspace] = useState('');
  const [showInput, setShowInput] = useState(true);
  const [tokenLoaded, setTokenLoaded] = useState(false);

  useEffect(() => {
    if (!tokenLoaded) {
      const savedToken = localStorageService.getItem('github_token');
      const savedOwner = localStorageService.getItem('github_owner');
      const savedRepo = localStorageService.getItem('github_repo');
      const savedTicketSource = localStorageService.getItem('ticket_source');
      const savedTicketWorkspace = localStorageService.getItem('ticket_workspace');
      
      if (savedToken && (savedToken.startsWith('ghp_') || savedToken.startsWith('github_pat_'))) {
        setToken(savedToken);
        onTokenChange(savedToken);
      }
      
      if (savedOwner) {
        setOwner(savedOwner);
        onOwnerChange(savedOwner);
      }
      
      if (savedRepo) {
        setRepo(savedRepo);
        onRepoChange(savedRepo);
      }
      
      if (savedTicketSource) {
        setTicketSource(savedTicketSource);
        onTicketSourceChange(savedTicketSource);
      } else {
        onTicketSourceChange('linear'); // Default to linear
      }
      
      if (savedTicketWorkspace) {
        setTicketWorkspace(savedTicketWorkspace);
        onTicketWorkspaceChange(savedTicketWorkspace);
      }
      
      if (savedToken && savedOwner && savedRepo && savedTicketWorkspace && (savedToken.startsWith('ghp_') || savedToken.startsWith('github_pat_'))) {
        setShowInput(false);
      }
      
      setTokenLoaded(true);
    }
  }, [tokenLoaded, onTokenChange, onOwnerChange, onRepoChange, onTicketSourceChange, onTicketWorkspaceChange]);

  useEffect(() => {
    if (token) {
      localStorageService.setItem('github_token', token);
      onTokenChange(token);
    } else {
      localStorageService.removeItem('github_token');
      onTokenChange('');
    }
  }, [token, onTokenChange]);

  useEffect(() => {
    if (owner) {
      localStorageService.setItem('github_owner', owner);
      onOwnerChange(owner);
    } else {
      localStorageService.removeItem('github_owner');
      onOwnerChange('');
    }
  }, [owner, onOwnerChange]);

  useEffect(() => {
    if (repo) {
      localStorageService.setItem('github_repo', repo);
      onRepoChange(repo);
    } else {
      localStorageService.removeItem('github_repo');
      onRepoChange('');
    }
  }, [repo, onRepoChange]);

  useEffect(() => {
    if (ticketSource) {
      localStorageService.setItem('ticket_source', ticketSource);
      onTicketSourceChange(ticketSource);
    }
  }, [ticketSource, onTicketSourceChange]);

  useEffect(() => {
    if (ticketWorkspace) {
      localStorageService.setItem('ticket_workspace', ticketWorkspace);
      onTicketWorkspaceChange(ticketWorkspace);
    } else {
      localStorageService.removeItem('ticket_workspace');
      onTicketWorkspaceChange('');
    }
  }, [ticketWorkspace, onTicketWorkspaceChange]);

  const isValidToken = token && (token.startsWith('ghp_') || token.startsWith('github_pat_'));
  const isValidOwner = owner && owner.trim().length > 0;
  const isValidRepo = repo && repo.trim().length > 0;
  const isValidTicketWorkspace = ticketWorkspace && ticketWorkspace.trim().length > 0;
  const allFieldsValid = isValidToken && isValidOwner && isValidRepo && isValidTicketWorkspace;

  const handleSave = () => {
    if (allFieldsValid) {
      setShowInput(false);
    }
  };

  const handleClear = () => {
    setToken('');
    setOwner('');
    setRepo('');
    setTicketSource('linear');
    setTicketWorkspace('');
    setShowInput(true);
  };

  if (allFieldsValid && !showInput) {
    return (
      <div className="token-configured">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: '5px 0',
          }}
        >
          <div
            style={{
              backgroundColor: '#e8f5e8',
              border: '1px solid #4caf50',
              borderRadius: '6px',
              padding: '4px 8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span style={{ color: '#2e7d32', fontWeight: 500 }}>✅ Configuration Saved</span>
            <small style={{ color: '#666' }}>
              {owner}/{repo} • {ticketSource} ({ticketWorkspace}) • (...{token.length > 4 ? token.slice(-4) : '****'})
            </small>
          </div>
          <button
            onClick={handleClear}
            style={{
              background: 'none',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '12px',
            }}
            title="Clear saved token"
          >
            🗑️ Clear
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="token-input">
      <h3>🔑 GitHub Configuration Required</h3>
      <p>Please enter your GitHub details to continue:</p>
      
      <div style={{ display: 'grid', gap: '10px', marginBottom: '10px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <input
            type="text"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            placeholder="Repository Owner (e.g., facebook)"
            style={{
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
          <input
            type="text"
            value={repo}
            onChange={(e) => setRepo(e.target.value)}
            placeholder="Repository Name (e.g., react)"
            style={{
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <select
            value={ticketSource}
            onChange={(e) => setTicketSource(e.target.value)}
            style={{
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          >
            <option value="linear">Linear</option>
            <option value="jira">Jira</option>
          </select>
          <input
            type="text"
            value={ticketWorkspace}
            onChange={(e) => setTicketWorkspace(e.target.value)}
            placeholder={ticketSource === 'linear' ? 'Linear workspace (e.g., mycompany)' : 'Jira domain (e.g., mycompany.atlassian.net)'}
            style={{
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
            style={{
              flex: 1,
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontSize: '14px',
            }}
          />
          <button
            onClick={handleSave}
            disabled={!allFieldsValid}
            style={{
              padding: '8px 16px',
              backgroundColor: allFieldsValid ? '#007bff' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: allFieldsValid ? 'pointer' : 'not-allowed',
              fontSize: '14px',
            }}
            title="Save configuration to browser storage"
          >
            💾 Save
          </button>
        </div>
      </div>

      <small style={{ color: '#666' }}>
        Create a token at{' '}
        <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer">
          https://github.com/settings/tokens
        </a>
      </small>

      {!isValidOwner && owner && (
        <div style={{ color: '#ff6b35', marginTop: '5px', fontSize: '14px' }}>
          ⚠️ Please enter a valid repository owner
        </div>
      )}
      
      {!isValidRepo && repo && (
        <div style={{ color: '#ff6b35', marginTop: '5px', fontSize: '14px' }}>
          ⚠️ Please enter a valid repository name
        </div>
      )}
      
      {!isValidTicketWorkspace && ticketWorkspace && (
        <div style={{ color: '#ff6b35', marginTop: '5px', fontSize: '14px' }}>
          ⚠️ Please enter a valid {ticketSource === 'linear' ? 'Linear workspace' : 'Jira domain'}
        </div>
      )}
      
      {token && !isValidToken && (
        <div style={{ color: '#ff6b35', marginTop: '5px', fontSize: '14px' }}>
          ⚠️ Token should start with 'ghp_' or 'github_pat_'
        </div>
      )}

      {allFieldsValid && (
        <div style={{ color: '#28a745', marginTop: '5px', fontSize: '14px' }}>
          ✅ All fields look valid - click Save to store configuration
        </div>
      )}
    </div>
  );
};
